import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import Title from '@/components/ui/Title';
import {
  cloneWebinar,
  deleteSessionAttendee,
  deleteWebinar,
  exportSessionAttendees,
  importSessionAttendees,
  fetchWebinarById,
  fetchWebinarSessionConfiguration,
  getWebinarSession,
  listModerators,
  listPresenters,
  listSessionAttendees,
  publishWebinar,
  unpublishWebinar,
} from '@/features/webinar/webinarThunks';
import { clearCurrentWebinar } from '@/features/webinar/webinarSlice';
import { selectHasFreeRegistrationLimit } from '@/features/workspace/workspaceSlice';
import FreeRegistrationLimitBanner, {
  FREE_REGISTRATION_LIMIT,
} from '@/components/billing/FreeRegistrationLimitBanner';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import LeftArrow from '@/components/icons/LeftArrow';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CircleX,
  Download,
  FileText,
  Loader2,
  LucideMessageCircleWarning,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import EditWebinar from './EditWebinar';
import CloneWebinarDialog, { CLONE_TITLE_MAX_LENGTH } from './CloneWebinarDialog';
import NotFoundPage from '@/pages/NotFound.tsx';
import IconNote from '@/components/icons/IconNote';
import { cn, getFormattedDate } from '@/lib/utils';
import { Pagination } from '@/components/ui/pagination';
import { toast } from 'react-toastify';
import { mapHttpError } from '@/lib/mapApiError';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import {
  buildAttendeesCsvPreview,
  resolveAttendeesCsvImportLimit,
  type AttendeesCsvPreview,
} from '@/lib/attendeesCsvPreview';
import type { AppError } from '@/features/auth/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const IMPORT_PREVIEW_ROWS_LIMIT = 50;

function CopyLinkCell({
  link,
  copyLabel,
  successLabel,
  emptyLabel,
}: {
  link?: string;
  copyLabel: string;
  successLabel: string;
  emptyLabel: string;
}) {
  // Lien absent : pas de faux lien, on garde une cellule cohérente
  if (!link) {
    return <span className="text-tertiary text-sm">{emptyLabel}</span>;
  }

  // Même affordance de copie que le dialog "Liens d'accès" (IconNote)
  return (
    <button
      type="button"
      title={copyLabel}
      aria-label={copyLabel}
      onClick={() => {
        void navigator.clipboard.writeText(link);
        toast.success(successLabel);
      }}
      className="cursor-pointer"
    >
      <IconNote size={24} color="#2c6cff" />
    </button>
  );
}

export default function WebinarSessionPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('webinars');
  const { webinarId } = useParams();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const workspaceTimezone = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.timezone,
  );
  const isConfigurationOnly =
    useAppSelector((state: RootState) => state.workspace.currentWorkspace?.productAccessStatus) ===
    'CONFIGURATION_ONLY';
  const hasFreeRegistrationLimit = useAppSelector(selectHasFreeRegistrationLimit);

  const [pageNumber, setPageNumber] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneTitle, setCloneTitle] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const { isLocked: isCloneLocked, runExclusive: runCloneExclusive } = useSubmitLock();
  const { isLocked: isImportLocked, runExclusive: runImportExclusive } = useSubmitLock();
  const [subscriptionIdPendingDeletion, setSubscriptionIdPendingDeletion] = useState<number | null>(
    null,
  );
  const [isDeletingAttendee, setIsDeletingAttendee] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<AttendeesCsvPreview | null>(null);
  const [isImportFileTooLarge, setIsImportFileTooLarge] = useState(false);
  const [isParsingImportFile, setIsParsingImportFile] = useState(false);
  const currentUser = useAppSelector((state: RootState) => state.auth.user);
  const {
    currentWebinarSession,
    currentWebinarSessionLoading,
    currentWebinarSessionError,
    currentWebinar,
    loading,
    error,
    sessionAttendees = [],
    sessionAttendeesTotalElements = 0,
    sessionAttendeesTotalPages = 0,
    sessionAttendeesLoading,
    presenters = [],
    sessionAttendeesExport,
    sessionAttendeesImport,
  } = useAppSelector((state: RootState) => state.webinar);

  const isCurrentUserPresenter =
    currentWebinar?.id != null &&
    presenters.some(
      (p) => p.webinarId === currentWebinar.id && String(p.userId) === String(currentUser?.id),
    );

  const sessionId =
    currentWebinarSession?.id ?? currentWebinar?.nextSessionId ?? currentWebinar?.lastSessionId;

  const isEvergreen = currentWebinarSession?.sessionType === 'EVERGREEN';

  const isManualSessionRefetch = useRef(false);

  const refetchSessionContext = useCallback(
    async (options?: { excludedSessionId?: number; realignWithNextSession?: boolean }) => {
      if (!workspaceId || !webinarId) return;

      isManualSessionRefetch.current = true;
      try {
        const wsId = Number(workspaceId);
        const wId = Number(webinarId);
        const updatedWebinar = await dispatch(
          fetchWebinarById({ workspaceId: wsId, id: wId }),
        ).unwrap();

        await dispatch(
          fetchWebinarSessionConfiguration({ workspaceId: wsId, webinarId: wId }),
        ).unwrap();

        const currentViewSessionId = currentWebinarSession?.id ?? null;
        let sessionIdToLoad: number | null = null;

        if (
          options?.excludedSessionId != null &&
          currentViewSessionId === options.excludedSessionId
        ) {
          sessionIdToLoad = updatedWebinar.nextSessionId ?? updatedWebinar.lastSessionId ?? null;
          if (sessionIdToLoad === options.excludedSessionId) {
            sessionIdToLoad = updatedWebinar.lastSessionId ?? updatedWebinar.nextSessionId ?? null;
          }
        } else if (options?.realignWithNextSession) {
          sessionIdToLoad =
            updatedWebinar.nextSessionId ?? updatedWebinar.lastSessionId ?? currentViewSessionId;
        } else {
          sessionIdToLoad =
            currentViewSessionId ??
            updatedWebinar.nextSessionId ??
            updatedWebinar.lastSessionId ??
            null;
        }

        if (sessionIdToLoad != null) {
          await dispatch(
            getWebinarSession({
              workspaceId: wsId,
              webinarId: wId,
              sessionId: sessionIdToLoad,
            }),
          ).unwrap();
        }
      } finally {
        isManualSessionRefetch.current = false;
      }
    },
    [dispatch, workspaceId, webinarId, currentWebinarSession?.id],
  );

  // Le webinaire précédemment ouvert est vidé du store avant de charger celui de l'URL, quel que
  // soit le point d'entrée (carte du dashboard, retour navigateur) : sinon ses quiz, sondages ou
  // offres restent affichés tant que les listes du nouveau ne sont pas revenues.
  const loadedWebinarIdRef = useRef(currentWebinar?.id);
  useEffect(() => {
    loadedWebinarIdRef.current = currentWebinar?.id;
  }, [currentWebinar?.id]);

  useEffect(() => {
    if (workspaceId && webinarId) {
      if (loadedWebinarIdRef.current != null && loadedWebinarIdRef.current !== Number(webinarId)) {
        dispatch(clearCurrentWebinar());
      }
      dispatch(
        fetchWebinarById({
          workspaceId: Number(workspaceId),
          id: Number(webinarId),
        }),
      );
      dispatch(
        fetchWebinarSessionConfiguration({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
        }),
      );
      dispatch(listPresenters({ workspaceId: Number(workspaceId), webinarId: Number(webinarId) }));
      dispatch(listModerators({ workspaceId: Number(workspaceId), webinarId: Number(webinarId) }));
    }
  }, [dispatch, workspaceId, webinarId]);

  // Debounce de la recherche pour ne pas requêter le back à chaque frappe
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const isWebinarLoaded = currentWebinar?.id === Number(webinarId);

  useEffect(() => {
    if (isManualSessionRefetch.current) return;
    if (workspaceId && webinarId && sessionId != null && isWebinarLoaded) {
      dispatch(
        getWebinarSession({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
          sessionId: Number(sessionId),
        }),
      );
      dispatch(
        listSessionAttendees({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
          sessionId: Number(sessionId),
          page: pageNumber,
          size: 10,
          search: debouncedSearch || undefined,
        }),
      );
    }
  }, [dispatch, workspaceId, webinarId, sessionId, pageNumber, debouncedSearch, isWebinarLoaded]);

  const isInitialLoading = (loading || currentWebinarSessionLoading) && !currentWebinarSession;
  const hasError = error || currentWebinarSessionError;

  if (isInitialLoading) {
    return (
      <div className="space-y-8">
        <Title level={1}>{t('sessionPage.title')}</Title>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (hasError) {
    const errorKey = error || currentWebinarSessionError || 'errors.generic';
    const isNotFound = errorKey === 'errors.notFound';
    return (
      <NotFoundPage
        fullscreen={false}
        title={isNotFound ? t('sessionPage.notFoundTitle') : t('sessionPage.errorTitle')}
        description={isNotFound ? t('sessionPage.notFoundDescription') : t(errorKey)}
        homeHref="/webinars"
        homeLabel={t('sessionPage.backToWebinars')}
      />
    );
  }

  const formattedPlannedDate = (() => {
    const dateToFormat = currentWebinarSession?.utcPlannedDate;
    if (!dateToFormat) return '';
    return getFormattedDate(dateToFormat, currentWebinarSession?.timezone || workspaceTimezone);
  })();

  const isPublished = currentWebinar?.status === 'PUBLISHED';

  const handleTogglePublish = async () => {
    if (!workspaceId || !currentWebinar?.id) return;
    try {
      if (isPublished) {
        await dispatch(
          unpublishWebinar({ workspaceId: Number(workspaceId), id: currentWebinar.id }),
        ).unwrap();
      } else {
        await dispatch(
          publishWebinar({ workspaceId: Number(workspaceId), id: currentWebinar.id }),
        ).unwrap();
      }
      setPublishDialogOpen(false);
    } catch (error) {
      const appError = error as AppError | undefined;

      if (appError?.details && Array.isArray(appError.details) && appError.details.length > 0) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            toast.error(t(mapHttpError(detail.message)));
          }
        });
      } else {
        toast.error(t(appError?.message ?? 'errors.generic'));
      }
    }
  };

  const handleDeleteWebinar = async () => {
    if (!workspaceId || !currentWebinar?.id) return;
    try {
      await dispatch(
        deleteWebinar({ workspaceId: Number(workspaceId), id: currentWebinar.id }),
      ).unwrap();
      setDeleteDialogOpen(false);
      navigate(-1);
    } catch (error) {
      // Le back refuse la suppression d'un webinaire source d'evergreen : quitter la page ferait
      // croire à une suppression effectuée. La modale se ferme pour laisser voir la notification.
      setDeleteDialogOpen(false);
      const appError = error as AppError | undefined;
      toast.error(t(mapHttpError(appError?.message ?? 'errors.generic')));
    }
  };

  const cloneWebinarSession = async () => {
    const sourceWebinarId = currentWebinar?.id;

    if (!workspaceId || !sourceWebinarId || !currentWebinar) {
      toast.error(t('sessionPage.clone.error'));
      return;
    }

    const newTitle = cloneTitle.trim();
    if (!newTitle) {
      toast.error(t('sessionPage.clone.errorTitleRequired'));
      return;
    }
    if (newTitle.length > CLONE_TITLE_MAX_LENGTH) {
      toast.error(t('sessionPage.clone.titleMaxLength'));
      return;
    }

    setIsCloning(true);
    try {
      const newWebinarId = await dispatch(
        cloneWebinar({
          workspaceId: Number(workspaceId),
          sourceWebinarId,
          newTitle,
        }),
      ).unwrap();
      setCloneDialogOpen(false);
      setCloneTitle('');
      toast.success(t('sessionPage.clone.success'));
      dispatch(clearCurrentWebinar());
      navigate(`/create-webinar/configuration/${newWebinarId}`);
    } catch (error) {
      const messageKey = (error as { message?: string } | undefined)?.message;
      toast.error(t(messageKey ?? 'sessionPage.clone.error'));
    } finally {
      setIsCloning(false);
    }
  };

  const handleCloneWebinar = () => void runCloneExclusive(cloneWebinarSession);

  // Le nombre d'inscrits du webinaire vit sur le DTO webinaire, pas sur la liste de la session.
  const refreshWebinarCounters = () => {
    if (!workspaceId || webinarId == null) return;
    dispatch(fetchWebinarById({ workspaceId: Number(workspaceId), id: Number(webinarId) }));
  };

  const handleConfirmDeleteAttendee = async () => {
    if (
      !workspaceId ||
      webinarId == null ||
      sessionId == null ||
      subscriptionIdPendingDeletion == null
    ) {
      return;
    }
    setIsDeletingAttendee(true);
    try {
      await dispatch(
        deleteSessionAttendee({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
          sessionId: Number(sessionId),
          subscriptionId: subscriptionIdPendingDeletion,
        }),
      ).unwrap();
      toast.success(t('sessionPage.attendees.deleteSuccess'));
      setSubscriptionIdPendingDeletion(null);
      refreshWebinarCounters();
    } catch {
      toast.error(t('sessionPage.attendees.deleteError'));
    } finally {
      setIsDeletingAttendee(false);
    }
  };

  const handleImportAttendeesFile = async (file: File): Promise<boolean> => {
    if (!workspaceId || !currentWebinar?.id || webinarId == null || sessionId == null) {
      toast.error(t('sessionPage.attendees.exportErrorNoSession'));
      return false;
    }
    try {
      const result = await dispatch(
        importSessionAttendees({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
          sessionId: Number(sessionId),
          file,
        }),
      ).unwrap();

      const duplicatesSuffix =
        result.duplicateRowsCount > 0
          ? t('sessionPage.attendees.importSuccessDuplicates', {
              count: result.duplicateRowsCount,
            })
          : '';
      toast.success(
        t('sessionPage.attendees.importSuccess', {
          created: result.createdCount,
          updated: result.updatedCount,
          unchanged: result.unchangedCount,
          duplicates: duplicatesSuffix,
        }),
      );

      dispatch(
        listSessionAttendees({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
          sessionId: Number(sessionId),
          page: pageNumber,
          size: 10,
          search: debouncedSearch || undefined,
        }),
      );
      refreshWebinarCounters();
      return true;
    } catch (error) {
      const appError = error as { message?: string; details?: unknown } | undefined;

      if (appError?.details && Array.isArray(appError.details) && appError.details.length > 0) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            toast.error(t(mapHttpError(detail.message)));
          }
        });
      } else if (appError?.message) {
        toast.error(t(mapHttpError(appError.message)));
      } else {
        toast.error(t('sessionPage.attendees.importError'));
      }
      return false;
    }
  };

  const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

  const resetImportDialogState = () => {
    setImportFile(null);
    setImportPreview(null);
    setIsImportFileTooLarge(false);
    setIsParsingImportFile(false);
  };

  const handleImportDialogOpenChange = (open: boolean) => {
    setIsImportDialogOpen(open);
    if (!open) {
      resetImportDialogState();
    }
  };

  const handleImportFileSelected = async (file: File | null) => {
    setImportFile(file);
    setImportPreview(null);
    if (!file) {
      setIsImportFileTooLarge(false);
      return;
    }
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setIsImportFileTooLarge(true);
      return;
    }
    setIsImportFileTooLarge(false);
    setIsParsingImportFile(true);
    try {
      const preview = await buildAttendeesCsvPreview(file);
      setImportPreview(preview);
    } finally {
      setIsParsingImportFile(false);
    }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    const success = await handleImportAttendeesFile(importFile);
    if (success) {
      setIsImportDialogOpen(false);
      resetImportDialogState();
    }
  };

  const handleConfirmImport = () => void runImportExclusive(confirmImport);

  const canConfirmImport =
    !!importFile &&
    !isImportFileTooLarge &&
    !isParsingImportFile &&
    !!importPreview &&
    !importPreview.unreadable &&
    !importPreview.missingFirstName &&
    !importPreview.missingEmail &&
    importPreview.headerWarnings.length === 0 &&
    importPreview.rowCount > 0 &&
    importPreview.invalidRows.length === 0 &&
    !sessionAttendeesImport.loading &&
    !isImportLocked;

  const importFreeLimit =
    hasFreeRegistrationLimit && importPreview
      ? resolveAttendeesCsvImportLimit(
          importPreview,
          currentWebinar?.registeredAttendeesCount,
          FREE_REGISTRATION_LIMIT,
        )
      : null;

  const handleExportAttendees = async () => {
    if (!workspaceId || !currentWebinar?.id || webinarId == null || sessionId == null) {
      toast.error(t('sessionPage.attendees.exportErrorNoSession'));
      return;
    }
    try {
      await dispatch(
        exportSessionAttendees({
          workspaceId: Number(workspaceId),
          webinarId: Number(webinarId),
          sessionId: Number(sessionId),
        }),
      ).unwrap();
      toast.success(t('sessionPage.attendees.exportSuccess'));
    } catch (error: unknown) {
      toast.error(
        error === 'EMPTY_SESSION_ATTENDEES_EXPORT'
          ? t('sessionPage.attendees.exportEmptyFile')
          : t('sessionPage.attendees.exportDownloadFailed'),
      );
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <Title level={1}>{t('sessionPage.title')}</Title>

        {isConfigurationOnly && (
          <div className="text-sm text-red-500 flex items-center gap-2 pt-6">
            <LucideMessageCircleWarning /> {t('sessionPage.configurationOnlyWarning')}
          </div>
        )}
      </div>
      <Separator />

      <div className="space-y-12">
        <div className="flex justify-between items-center">
          <div className="flex items-start gap-2">
            <Link to="/webinars" className="py-1 ">
              <LeftArrow className="size-6" color="#0C0E21" />
            </Link>
            <div className="space-y-2">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-semibold">{currentWebinarSession?.webinarTitle}</h2>
                <Badge
                  variant={currentWebinarSession?.sessionType === 'LIVE' ? 'live' : 'evergreen'}
                  className="text-normal font-medium"
                >
                  {currentWebinarSession?.sessionType === 'LIVE'
                    ? t('sessionPage.sessionType.live')
                    : t('sessionPage.sessionType.evergreen')}
                </Badge>
                <Badge
                  variant={currentWebinar?.status === 'PUBLISHED' ? 'published' : 'draft'}
                  className="text-normal font-medium"
                >
                  {currentWebinar?.status === 'PUBLISHED'
                    ? t('sessionPage.status.published')
                    : t('sessionPage.status.draft')}
                </Badge>
                {currentWebinarSession?.status === 'FINISHED' && (
                  <Badge variant="finished" className="text-normal font-medium">
                    {t('sessionPage.status.finished')}
                  </Badge>
                )}
              </div>
              <p className="text-secondary text-sm">{formattedPlannedDate}</p>
              {currentWebinarSession?.status !== 'FINISHED' && (
                <div className="flex items-center gap-2">
                  <Button
                    // blank target
                    onClick={() => {
                      window.open(currentWebinar?.registrationUrl, '_blank');
                    }}
                  >
                    {t('sessionPage.registrationPage')}
                  </Button>
                  {isCurrentUserPresenter && currentWebinarSession?.sessionType === 'LIVE' && (
                    <Link
                      to={`/admin-live/${currentWebinar?.id}/${currentWebinarSession?.id}`}
                      target="_blank"
                      className={cn(buttonVariants({ variant: 'default' }))}
                    >
                      {t('sessionPage.testAndLaunchLive')}
                    </Link>
                  )}
                </div>
              )}
              {currentWebinarSession?.sessionType === 'EVERGREEN' &&
                currentWebinarSession?.status === 'IN_PROGRESS' && (
                  <p className="text-sm font-normal text-red-600 max-w-xl">
                    {t('sessionPage.evergreenInProgressNotice')}
                  </p>
                )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center gap-2 cursor-pointer">
                  {isPublished ? (
                    <CircleX size={24} className="text-secondary" />
                  ) : (
                    <Download size={24} className="text-secondary" />
                  )}

                  <p className="text-sm font-medium text-secondary">
                    {isPublished
                      ? t('sessionPage.publish.unpublish')
                      : t('sessionPage.publish.action')}
                  </p>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {isPublished
                      ? t('sessionPage.publish.unpublishConfirmTitle')
                      : t('sessionPage.publish.confirmTitle')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    {isPublished
                      ? t('sessionPage.publish.unpublishWarning2')
                      : t('sessionPage.publish.publishWarning')}
                  </div>
                </div>
                <DialogFooter className="w-full flex justify-between gap-4">
                  <DialogTrigger asChild>
                    <div className="w-1/2">
                      <Button variant="outline" className="w-full">
                        {t('sessionPage.cancel')}
                      </Button>
                    </div>
                  </DialogTrigger>
                  <div className="w-1/2">
                    <Button onClick={handleTogglePublish} className="w-full">
                      {isPublished
                        ? t('sessionPage.publish.unpublishConfirmButton')
                        : t('sessionPage.publish.confirmButton')}
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <CloneWebinarDialog
              open={cloneDialogOpen}
              onOpenChange={(open) => {
                if (isCloning) return;
                setCloneDialogOpen(open);
                if (!open) setCloneTitle('');
              }}
              title={cloneTitle}
              onTitleChange={setCloneTitle}
              sourceTitle={currentWebinar?.title ?? ''}
              isEvergreen={isEvergreen}
              isCloning={isCloning}
              isLocked={isCloneLocked}
              onConfirm={handleCloneWebinar}
            />
            {/* <button className="flex items-center gap-2 cursor-pointer">
              <EditIcon size={24} className="text-secondary" />
              <p className="text-sm font-medium text-secondary">Modifier</p>
            </button> */}

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center gap-2 cursor-pointer">
                  <Trash2 size={24} className="text-secondary" />
                  <p className="text-sm font-medium text-secondary">
                    {t('sessionPage.delete.action')}
                  </p>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('sessionPage.delete.confirmTitle')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>{t('sessionPage.delete.warning1')}</div>
                  <div>{t('sessionPage.delete.warning2')}</div>
                </div>
                <DialogFooter className="w-full flex justify-between gap-4">
                  <DialogTrigger asChild>
                    <div className="w-1/2">
                      <Button variant="outline" className="w-full">
                        {t('sessionPage.cancel')}
                      </Button>
                    </div>
                  </DialogTrigger>
                  <div className="w-1/2">
                    <Button onClick={handleDeleteWebinar} className="w-full">
                      {t('sessionPage.delete.confirmButton')}
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <EditWebinar webinar={currentWebinar} onSessionsChanged={refetchSessionContext} />

        <Separator />

        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <Label className="text-[20px] font-medium">
              {t('sessionPage.attendees.title')}{' '}
              <span className="text-tertiary text-normal">
                ({sessionAttendeesLoading ? '...' : sessionAttendeesTotalElements})
              </span>
            </Label>
            <div className="flex items-center gap-3">
              <Dialog open={isImportDialogOpen} onOpenChange={handleImportDialogOpenChange}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-fit">
                    <Upload size={24} />
                    <p className="text-sm font-medium ">{t('sessionPage.attendees.import')}</p>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('sessionPage.attendees.importDialog.title')}</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-6">
                    <div className="space-y-2 text-base">
                      <p className="text-tertiary">
                        {t('sessionPage.attendees.importDialog.instructionsFormat')}
                      </p>
                      <p className="flex flex-wrap items-center gap-1.5">
                        <span className="text-tertiary">
                          {t('sessionPage.attendees.importDialog.instructionsRequired')}
                        </span>
                        <span className="font-semibold">Prenom</span>
                        <span className="text-tertiary">
                          {t('sessionPage.attendees.importDialog.or')}
                        </span>
                        <span className="font-semibold">FirstName</span>
                        <span className="text-tertiary">+</span>
                        <span className="font-semibold">Email</span>
                      </p>
                      <p className="flex flex-wrap items-center gap-1.5">
                        <span className="text-tertiary">
                          {t('sessionPage.attendees.importDialog.instructionsOptional')}
                        </span>
                        <span className="font-semibold">Nom</span>,
                        <span className="font-semibold">Telephone</span>
                      </p>
                      <p className="text-tertiary text-sm">
                        {t('sessionPage.attendees.importDialog.instructionsDuplicate')}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <input
                        type="file"
                        id="attendees-import-dialog-input"
                        accept=".csv,text/csv"
                        className="sr-only"
                        onChange={(event) => {
                          const file = event.target.files?.[0] ?? null;
                          event.target.value = '';
                          void handleImportFileSelected(file);
                        }}
                      />
                      <Label
                        htmlFor="attendees-import-dialog-input"
                        className="cursor-pointer p-4 bg-white border border-input rounded-2xl flex items-center gap-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="size-10 shrink-0 bg-medium rounded-lg flex items-center justify-center">
                          <FileText size={20} />
                        </div>
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="truncate font-medium">
                            {importFile
                              ? t('sessionPage.attendees.importDialog.changeFile')
                              : t('sessionPage.attendees.importDialog.chooseFile')}
                          </span>
                          <span className="text-sm text-tertiary truncate">
                            {importFile
                              ? importFile.name
                              : t('sessionPage.attendees.importDialog.noFileSelected')}
                          </span>
                        </div>
                      </Label>
                    </div>

                    {isParsingImportFile && (
                      <div className="flex items-center gap-2 text-sm text-tertiary">
                        <Loader2 size={16} className="animate-spin" />
                        {t('sessionPage.attendees.importDialog.recapTitle')}
                      </div>
                    )}

                    {isImportFileTooLarge && (
                      <div className="flex items-center gap-2 text-sm text-error">
                        <AlertTriangle size={16} className="shrink-0" />
                        {t('sessionPage.attendees.importDialog.errorTooLarge')}
                      </div>
                    )}

                    {importPreview && !isParsingImportFile && (
                      <div className="space-y-3">
                        <p className="font-medium">
                          {t('sessionPage.attendees.importDialog.recapTitle')}
                        </p>

                        {importPreview.unreadable ? (
                          <div className="flex items-center gap-2 text-sm text-error">
                            <AlertTriangle size={16} className="shrink-0" />
                            {t('sessionPage.attendees.importDialog.errorUnreadable')}
                          </div>
                        ) : (
                          <>
                            {importPreview.missingFirstName && (
                              <div className="flex items-center gap-2 text-sm text-error">
                                <AlertTriangle size={16} className="shrink-0" />
                                {t('sessionPage.attendees.importDialog.errorMissingFirstName')}
                              </div>
                            )}
                            {importPreview.missingEmail && (
                              <div className="flex items-center gap-2 text-sm text-error">
                                <AlertTriangle size={16} className="shrink-0" />
                                {t('sessionPage.attendees.importDialog.errorMissingEmail')}
                              </div>
                            )}
                            {importPreview.headerWarnings.length > 0 && (
                              <div className="rounded-xl border border-error/40 bg-error/5 p-3 space-y-2">
                                {importPreview.headerWarnings.map((warning) => (
                                  <div
                                    key={warning.header}
                                    className="flex items-start gap-2 text-sm text-error"
                                  >
                                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                    <span>
                                      {t('sessionPage.attendees.importDialog.errorHeaderAccent', {
                                        header: warning.header,
                                        suggestion: warning.suggestion,
                                      })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {!importPreview.missingFirstName && !importPreview.missingEmail && (
                              <p className="text-sm text-tertiary">
                                {importPreview.rowCount > 0
                                  ? t('sessionPage.attendees.importDialog.recapRowCount', {
                                      count: importPreview.rowCount,
                                    })
                                  : t('sessionPage.attendees.importDialog.recapNoRows')}
                              </p>
                            )}

                            {importFreeLimit && (
                              <div className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning-light/50 p-3 text-sm">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5 text-warning" />
                                <span>
                                  {t('sessionPage.attendees.importDialog.warningFreeLimit', {
                                    max: FREE_REGISTRATION_LIMIT,
                                    remaining: importFreeLimit.remainingSlots,
                                    refused: importFreeLimit.refusedRowCount,
                                  })}
                                </span>
                              </div>
                            )}

                            {!importPreview.missingFirstName &&
                              !importPreview.missingEmail &&
                              importPreview.rows.length > 0 && (
                                <div className="rounded-xl border border-input overflow-hidden">
                                  <div className="max-h-64 overflow-y-auto">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>
                                            {t('sessionPage.attendees.importDialog.lineColumn')}
                                          </TableHead>
                                          <TableHead>
                                            {t('sessionPage.attendees.firstName')}
                                          </TableHead>
                                          <TableHead>{t('sessionPage.attendees.name')}</TableHead>
                                          <TableHead>{t('sessionPage.attendees.email')}</TableHead>
                                          <TableHead>{t('sessionPage.attendees.phone')}</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {importPreview.rows
                                          .slice(0, IMPORT_PREVIEW_ROWS_LIMIT)
                                          .map((row) => (
                                            <TableRow
                                              key={row.line}
                                              className={cn(
                                                row.error
                                                  ? 'bg-error/10'
                                                  : row.isDuplicateEmail && 'bg-error/5',
                                              )}
                                            >
                                              <TableCell>{row.line}</TableCell>
                                              <TableCell className="truncate max-w-32">
                                                {row.firstName || '-'}
                                              </TableCell>
                                              <TableCell className="truncate max-w-32">
                                                {row.lastName || '-'}
                                              </TableCell>
                                              <TableCell className="truncate max-w-48">
                                                {row.email || '-'}
                                                {row.error && (
                                                  <div className="flex items-center gap-1 text-xs text-error mt-0.5">
                                                    <AlertTriangle size={12} className="shrink-0" />
                                                    {t(
                                                      `sessionPage.attendees.importDialog.rowError.${row.error}`,
                                                    )}
                                                  </div>
                                                )}
                                              </TableCell>
                                              <TableCell className="truncate max-w-32">
                                                {row.phone || '-'}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                  {importPreview.rows.length > IMPORT_PREVIEW_ROWS_LIMIT && (
                                    <p className="text-sm text-tertiary p-2 border-t border-input">
                                      {t('sessionPage.attendees.importDialog.recapMoreRows', {
                                        count:
                                          importPreview.rows.length - IMPORT_PREVIEW_ROWS_LIMIT,
                                      })}
                                    </p>
                                  )}
                                </div>
                              )}

                            {importPreview.invalidRows.length > 0 && (
                              <div className="rounded-xl border border-error/40 bg-error/5 p-3 space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium text-error">
                                  <AlertTriangle size={16} className="shrink-0" />
                                  {t('sessionPage.attendees.importDialog.invalidRowsTitle')} (
                                  {importPreview.invalidRows.length})
                                </div>
                                <p className="text-sm text-tertiary">
                                  {t('sessionPage.attendees.importDialog.invalidRowsDescription')}
                                </p>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                  {importPreview.invalidRows.map((row) => (
                                    <div key={row.line} className="text-sm flex items-center gap-2">
                                      <span className="font-semibold shrink-0">
                                        {t('sessionPage.attendees.importDialog.lineLabel', {
                                          line: row.line,
                                        })}
                                      </span>
                                      <span className="text-tertiary truncate">
                                        {t(
                                          `sessionPage.attendees.importDialog.rowError.${row.error}`,
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {importPreview.duplicates.length > 0 && (
                              <div className="rounded-xl border border-input p-3 space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium text-error">
                                  <AlertTriangle size={16} className="shrink-0" />
                                  {t('sessionPage.attendees.importDialog.duplicatesTitle')} (
                                  {importPreview.duplicates.length})
                                </div>
                                <p className="text-sm text-tertiary">
                                  {t('sessionPage.attendees.importDialog.duplicatesDescription')}
                                </p>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                  {importPreview.duplicates.map((duplicate) => (
                                    <div
                                      key={duplicate.email}
                                      className="text-sm flex items-center justify-between gap-2"
                                    >
                                      <span className="truncate">{duplicate.email}</span>
                                      <span className="text-tertiary shrink-0">
                                        {t('sessionPage.attendees.importDialog.duplicatesLines', {
                                          lines: duplicate.lines.join(', '),
                                        })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <DialogFooter className="gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleImportDialogOpenChange(false)}
                      disabled={sessionAttendeesImport.loading}
                    >
                      {t('sessionPage.attendees.importDialog.cancel')}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleConfirmImport}
                      disabled={!canConfirmImport}
                    >
                      {(sessionAttendeesImport.loading || isImportLocked) && (
                        <Loader2 className="animate-spin" />
                      )}
                      {sessionAttendeesImport.loading || isImportLocked
                        ? t('sessionPage.attendees.importDialog.importing')
                        : t('sessionPage.attendees.importDialog.confirm')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button
                variant="outline"
                className="w-fit"
                disabled={sessionAttendeesExport.loading || sessionAttendeesLoading}
                onClick={() => void handleExportAttendees()}
              >
                <Download size={24} className="" />
                <p className="text-sm font-medium ">{t('sessionPage.attendees.export')}</p>
              </Button>
            </div>
            {/*
            <div className="flex items-center gap-4">
              <button className="flex items-center gap-2 cursor-pointer">
                <ArrowDownUp size={24} className="" />
                <p className="text-sm font-medium ">Ordonner par</p>
              </button>

              <button className="flex items-center gap-2 cursor-pointer">
                <Funnel />
                <p className="text-sm font-medium ">Filtrer</p>
              </button>
            </div> */}
          </div>

          {hasFreeRegistrationLimit && (
            <FreeRegistrationLimitBanner
              registeredAttendeesCount={currentWebinar?.registeredAttendeesCount}
              limitReached={currentWebinar?.freeRegistrationLimitReached}
            />
          )}

          <div className="relative w-full max-w-md">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
            />
            <Input
              type="text"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPageNumber(0);
              }}
              placeholder={t('sessionPage.attendees.searchPlaceholder')}
              className="pl-10 pr-10 h-11"
            />
            {sessionAttendeesLoading ? (
              <Loader2
                size={18}
                className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-secondary"
              />
            ) : (
              search && (
                <button
                  type="button"
                  aria-label={t('sessionPage.attendees.searchClear')}
                  onClick={() => {
                    setSearch('');
                    setPageNumber(0);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary cursor-pointer"
                >
                  <CircleX size={18} />
                </button>
              )
            )}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('sessionPage.attendees.registrationDate')}</TableHead>
                <TableHead>{t('sessionPage.attendees.email')}</TableHead>
                <TableHead>{t('sessionPage.attendees.firstName')}</TableHead>
                <TableHead>{t('sessionPage.attendees.phone')}</TableHead>
                <TableHead className="text-center">{t('sessionPage.attendees.liveLink')}</TableHead>
                <TableHead className="text-center">
                  {t('sessionPage.attendees.replayLink')}
                </TableHead>
                <TableHead className="text-center">{t('sessionPage.attendees.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              className={cn(
                'transition-opacity',
                sessionAttendeesLoading && sessionAttendees.length > 0 && 'opacity-50',
              )}
            >
              {sessionAttendeesLoading && sessionAttendees.length === 0 ? (
                <TableRow>
                  <TableCell>{t('sessionPage.attendees.loading')}</TableCell>
                </TableRow>
              ) : sessionAttendees.length === 0 ? (
                <TableRow>
                  <TableCell>
                    {debouncedSearch
                      ? t('sessionPage.attendees.emptySearch')
                      : t('sessionPage.attendees.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                sessionAttendees.map((attendee) => (
                  <TableRow key={attendee.attendeeId}>
                    <TableCell>
                      <p className="text-secondary text-sm">
                        {attendee.registeredAt
                          ? DateTime.fromISO(attendee.registeredAt).toFormat('dd/MM/yyyy')
                          : '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-secondary text-sm">{attendee.email}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-normal font-medium">{attendee.firstName}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-secondary text-sm">{attendee.phoneNumber}</p>
                    </TableCell>
                    <TableCell className="text-center">
                      <CopyLinkCell
                        link={attendee.liveLink}
                        copyLabel={t('access.copy')}
                        successLabel={t('access.copySuccess')}
                        emptyLabel={t('sessionPage.attendees.noLink')}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <CopyLinkCell
                        link={attendee.replayLink}
                        copyLabel={t('access.copy')}
                        successLabel={t('access.copySuccess')}
                        emptyLabel={t('sessionPage.attendees.noLink')}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        type="button"
                        onClick={() => setSubscriptionIdPendingDeletion(attendee.subscriptionId)}
                        aria-label={t('sessionPage.attendees.delete')}
                        className="p-1.5 rounded-lg cursor-pointer text-secondary hover:text-error hover:bg-medium transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="w-full">
            <Pagination
              currentPage={pageNumber}
              totalPages={sessionAttendeesTotalPages}
              onPageChange={setPageNumber}
            />
          </div>
        </div>
      </div>

      <AlertDialog
        open={subscriptionIdPendingDeletion != null}
        onOpenChange={(open) => {
          if (!open) setSubscriptionIdPendingDeletion(null);
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sessionPage.attendees.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sessionPage.attendees.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingAttendee}>
              {t('sessionPage.attendees.deleteConfirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDeleteAttendee();
              }}
              disabled={isDeletingAttendee}
            >
              {t('sessionPage.attendees.deleteConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
